import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import FilterListIcon from '@mui/icons-material/FilterList'
import type { GridColDef, GridFilterItem, GridFilterModel } from '@mui/x-data-grid-premium'
import COLORS from 'lib/colors'
import { LookupMap } from './leadTableTypes'
import { LeadTableDateFilterValue } from '../filters/LeadTableDateFilter'
import moment from 'moment-timezone'
import { useTimezone } from 'lib/TimezoneProvider'

type LeadTableFilterBarProps = {
  filterModel: GridFilterModel
  columns: GridColDef[]
  onFilterModelChange: (model: GridFilterModel) => void
  clientIdToName: LookupMap
  clientCampaignIdToName: LookupMap
  vendorIdToName: LookupMap
  degreeProgramIdToName: LookupMap
  statuses: LookupMap
  dispositions: LookupMap
}

function isDateRangeFilterValue(value: unknown): value is LeadTableDateFilterValue {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'operator' in value &&
      'value' in value &&
      value.operator === 'between' &&
      (value as LeadTableDateFilterValue).value &&
      typeof (value as LeadTableDateFilterValue).value === 'object'
  )
}

function dateFilterPartToString(value: unknown) {
  if (typeof value === 'string') return value

  return null
}

function isHiddenDefaultDateFilter(item: GridFilterItem) {
  return (
    item.field === 'createdAt' &&
    item.operator === 'between' &&
    isDateRangeFilterValue(item.value) &&
    item.value.value.label === 'Today'
  )
}

function filterHasCompleteValue(item: GridFilterItem) {
  if (item.operator === 'isEmpty' || item.operator === 'isNotEmpty') return true

  if (isDateRangeFilterValue(item.value)) {
    if (item.value.value.label) return true

    const [rawStart, rawEnd] = item.value.value.timeRange ?? []

    return Boolean(dateFilterPartToString(rawStart) && dateFilterPartToString(rawEnd))
  }

  if (Array.isArray(item.value)) return item.value.length > 0

  return item.value !== null && item.value !== undefined && item.value !== ''
}

export function getVisibleLeadTableFilters(filterModel: GridFilterModel) {
  return filterModel.items.filter((item) => {
    if (!item.field || !item.operator) return false
    if (!filterHasCompleteValue(item)) return false
    if (isHiddenDefaultDateFilter(item)) return false

    return true
  })
}

function formatFilterOperator(operator: GridFilterItem['operator']) {
  switch (operator) {
    case 'isAnyOf':
      return 'is any of'
    case 'isNotAnyOf':
      return 'is not any of'
    case 'doesNotContain':
      return 'does not contain'
    case 'doesNotEqual':
      return 'does not equal'
    case 'startsWith':
      return 'starts with'
    case 'endsWith':
      return 'ends with'
    case 'isEmpty':
      return 'is empty'
    case 'isNotEmpty':
      return 'is not empty'
    case 'onOrAfter':
      return 'on or after'
    case 'onOrBefore':
      return 'on or before'
    default:
      return operator
  }
}

function formatDate(value: string, boundary: 'start' | 'end', timezone: string) {
  const date = moment.parseZone(value).tz(timezone)

  if (boundary === 'start') {
    const displayMoment = date.clone().startOf('hour')
    const dateText = displayMoment.format('M/D/YY')

    if (displayMoment.isSame(displayMoment.clone().startOf('day'))) {
      return dateText
    }

    return `${dateText}, ${displayMoment.format('hA')}`
  }

  const isEndOfDay =
    date.hour() === 23 &&
    date.minute() === 59 &&
    date.second() === 59

  if (isEndOfDay) {
    return date.format('M/D/YY')
  }

  const displayMoment = date.clone().add(1, 'hour').startOf('hour')
  const dateText = displayMoment.format('M/D/YY')

  return `${dateText}, ${displayMoment.format('hA')}`
}

function formatDateRangeFilterValue(value: unknown, timezone: string) {
  if (!isDateRangeFilterValue(value)) return value

  if (value.value.label) {
    return value.value.label
  }

  const [rawStart, rawEnd] = value.value.timeRange ?? []
  const start = dateFilterPartToString(rawStart)
  const end = dateFilterPartToString(rawEnd)

  if (!start || !end) return value

  return `${formatDate(start, 'start', timezone)} - ${formatDate(end, 'end', timezone)}`
}

function shouldReplaceFilterOperator(value: unknown) {
  return isDateRangeFilterValue(value) && Boolean(value.value.label)
}

export default function LeadTableFilterBar({
  filterModel,
  columns,
  onFilterModelChange,
  clientIdToName,
  clientCampaignIdToName,
  vendorIdToName,
  degreeProgramIdToName,
  statuses,
  dispositions,
}: LeadTableFilterBarProps) {
  const { timezone } = useTimezone()
  const filters = getVisibleLeadTableFilters(filterModel)

  if (filters.length === 0) return null

  const columnsByField = new Map(columns.map((column) => [column.field, column]))

  const lookupMaps: Partial<Record<string, Record<string | number, string>>> = {
    clientId: clientIdToName,
    clientCampaignId: clientCampaignIdToName,
    vendorId: vendorIdToName,
    degreeProgramId: degreeProgramIdToName,
    status: statuses,
    disposition: dispositions,
  }

  function displayFilterValue(field: string, value: unknown) {
    const column = columnsByField.get(field)
    if (column && 'isBooleanColumn' in column && (value === 'true' || value === 'false')) {
      return value === 'true' ? 'Yes' : 'No'
    }

    const dateRangeValue = formatDateRangeFilterValue(value, timezone)
    if (dateRangeValue !== value) return dateRangeValue

    const lookup = lookupMaps[field]

    if (!lookup || value == null) return value

    return lookup[value as string | number] ?? value
  }

  function handleDeleteFilter(id: GridFilterModel['items'][number]['id']) {
    onFilterModelChange({
      ...filterModel,
      items: filterModel.items.filter((item) => item.id !== id),
    })
  }

  return (
    <Stack
      direction='row'
      alignItems='flex-start'
      spacing={1}
      sx={{
        minHeight: 48,
        width: '100%',
        px: 2,
        py: '10px',
        flexShrink: 0,
      }}
    >
      <Tooltip title='Active filters'>
        <FilterListIcon
          sx={{
            color: COLORS.frenchBlue,
            fontSize: 18,
            height: 28,
          }}
        />
      </Tooltip>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          flex: 1,
          '& .MuiChip-root': {
            height: 28,
          },
        }}
      >
        {filters.map((filter) => {
          const column = columnsByField.get(filter.field)
          const label = column?.headerName ?? filter.field
          const isArrayValue = Array.isArray(filter.value)

          const tooltipValues = isArrayValue
            ? filter.value.map((value) => displayFilterValue(filter.field, value))
            : []

          const value = isArrayValue ? '...' : displayFilterValue(filter.field, filter.value)

          const hasValue = value !== null && value !== undefined && value !== ''

          const valueSuffix = isArrayValue ? '...' : hasValue ? ` ${value}` : ''

          const chipLabel = shouldReplaceFilterOperator(filter.value)
            ? `${label} is ${valueSuffix}`
            : `${label} ${formatFilterOperator(filter.operator)}${valueSuffix}`

          const chip = (
            <Chip
              key={filter.id ?? `${filter.field}-${filter.operator}-${String(value)}`}
              label={chipLabel}
              onDelete={() => handleDeleteFilter(filter.id)}
              size='small'
            />
          )

          if (!isArrayValue) return chip

          return (
            <Tooltip
              key={filter.id ?? `${filter.field}-${filter.operator}`}
              arrow
              title={
                <Box>
                  <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                    {chipLabel}
                  </Typography>

                  <Box
                    component='ul'
                    sx={{
                      m: 0,
                      pl: 0,
                      listStyle: 'none',
                    }}
                  >
                    {tooltipValues.map((tooltipValue) => (
                      <Box
                        component='li'
                        key={String(tooltipValue)}
                        sx={{
                          '&::before': {
                            content: '"- "',
                          },
                        }}
                      >
                        <Typography variant='body2' component='span'>
                          {String(tooltipValue)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              }
            >
              {chip}
            </Tooltip>
          )
        })}
      </Box>
    </Stack>
  )
}
